import { ComponentChild } from 'preact';
import clsx from 'clsx';
import { variationName } from 'utilities/css';
import { useDisableClick } from 'utilities/interactions';
import { useUniqueId } from 'utilities/use-unique-id';

import { Icon } from 'components/Icon';
import { Spinner } from 'components/Spinner';

import styles from './Button.module.scss';

type IconPlacement = 'left' | 'right';

interface ButtonProps {
  loading?: boolean;
  disabled?: boolean;
  outline?: boolean;
  primary?: boolean;
  rounded?: boolean;
  compact?: boolean;
  shadow?: boolean;
  icon?: ReturnType<typeof Icon>;
  iconPlacement?: IconPlacement;
  plain?: boolean;
  url?: string;
  external?: boolean;
  noUnderline?: boolean;
  accessibilityLabel?: string;
  children?: ComponentChild;
  monochrome?: boolean;
  onClick?: () => void;
}

export function Button({
  loading,
  disabled,
  primary,
  rounded,
  compact,
  shadow,
  icon,
  iconPlacement = 'left',
  plain,
  url,
  external,
  noUnderline,
  accessibilityLabel,
  children,
  monochrome,
  onClick,
}: ButtonProps) {
  const id = useUniqueId('Button');
  const isDisabled = loading || disabled;
  const className = clsx(
    styles.Button,
    primary && styles.primary,
    rounded && styles.rounded,
    compact && styles.compact,
    shadow && styles.hasShadow,
    isDisabled && styles.disabled,
    loading && styles.loading,
    (plain || url) && styles.plain,
    plain && noUnderline && styles.noUnderline,
    plain && monochrome && styles.monochrome,
    icon && !children && styles.iconOnly,
    icon && styles.withIcon,
    icon && styles[variationName('iconPlacement', iconPlacement)],
  );

  const iconSource = icon ? icon : null;

  const iconMarkup = iconSource ? (
    <span className={clsx(styles.Icon, loading && styles.hidden)}>
      {iconSource}
    </span>
  ) : null;

  const spinnerMarkup = loading ? (
    <div className={styles.Spinner}>
      <Spinner size="small" />
    </div>
  ) : null;

  const handleClick = useDisableClick(disabled, onClick);

  const textContentMarkup = children ? (
    <span className={styles.Text}>{children}</span>
  ) : null;

  const commonProps = {
    id,
    className,
  };

  const contentMarkup = (
    <span className={clsx(styles.Content, loading && styles.hidden)}>
      {iconMarkup}
      {textContentMarkup}
    </span>
  );

  if (url) {
    const target = external ? '_blank' : undefined;
    const rel = external ? 'noopener noreferrer' : undefined;

    return (
      <a
        href={url}
        {...commonProps}
        target={target}
        rel={rel}
        aria-label={accessibilityLabel}
      >
        {contentMarkup}
        {spinnerMarkup}
      </a>
    );
  }

  return (
    <button
      {...commonProps}
      aria-label={accessibilityLabel}
      aria-busy={loading ? true : undefined}
      aria-disabled={isDisabled ? true : undefined}
      disabled={isDisabled}
      tabIndex={disabled ? -1 : undefined}
      onClick={handleClick}
    >
      {contentMarkup}
      {spinnerMarkup}
    </button>
  );
}
